import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTutorial } from '../TutorialContext';
import { useAuth } from '../../../context/AuthContext';
import { scrollToTutorialContent } from '../utils/scrollToTutorialContent';
import './TutorialProgressBanner.css';

const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronUp = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 15L12 9L6 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Check = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Close = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function TutorialProgressBanner() {
  const { completedSteps, skippedSteps = [], dismissTutorial, isDismissed, skipStep, isLoaded } = useTutorial();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const isOwner = user?.role === 'owner';

  const handleNavigate = (path, state) => {
    sessionStorage.setItem('tutorialNavigation', 'true');
    navigate(path, state);
    scrollToTutorialContent(path);
  };

  const steps = [
    {
      id: 1,
      title: 'Company Profile',
      description: 'Set up your business information',
      onClick: () => handleNavigate('/app/overview/settings', { state: { section: 'business', parent: 'business', fromTutorial: true, tutorialStep: 1 } })
    },
    {
      id: 2,
      title: 'Business Hours',
      description: 'Configure your working hours',
      onClick: () => handleNavigate('/app/overview/settings', { state: { section: 'hours', parent: 'hours', fromTutorial: true, tutorialStep: 2 } })
    },
    {
      id: 3,
      title: 'Add Services',
      description: 'Add your services and pricing',
      onClick: () =>
        handleNavigate('/app/overview/settings', {
          state: {
            section: 'services-categories',
            parent: 'services-categories',
            servicesTab: 'services',
            fromTutorial: true,
            tutorialStep: 3,
          },
        })
    },
    {
      id: 4,
      title: 'Add Employees',
      description: 'Invite your team members',
      onClick: () => handleNavigate('/app/overview/company-management', { state: { fromTutorial: true, tutorialStep: 4 } })
    },
    {
      id: 5,
      title: 'Booking rules',
      description: 'Set lead times, double booking, and other scheduling rules',
      onClick: () => handleNavigate('/app/overview/settings', { state: { section: 'booking', parent: 'booking', fromTutorial: true, tutorialStep: 5 } })
    },
    {
      id: 6,
      title: 'Set Reminders',
      description: 'Configure email/SMS reminders',
      onClick: () =>
        handleNavigate('/app/overview/settings', {
          state: {
            section: 'notifications',
            parent: 'notifications',
            messagingTab: 'sms',
            fromTutorial: true,
            tutorialStep: 6,
          },
        })
    },
    {
      id: 7,
      title: 'Set Up Client Experience',
      description: 'Launch your website and enable portal access for returning clients',
      onClick: () => handleNavigate('/app/overview/settings', { state: { section: 'client-experience', parent: 'client-experience', fromTutorial: true, tutorialStep: 7 } })
    },
    {
      id: 8,
      title: 'Payment Settings',
      description: 'Connect Deluxe',
      onClick: () => handleNavigate('/app/overview/settings', { state: { section: 'authorize', parent: 'payments', fromTutorial: true, tutorialStep: 8 } })
    }
  ];

  const allStepsCompleted = steps.every(step => completedSteps.includes(step.id) || skippedSteps.includes(step.id));
  const doneCount = completedSteps.length + (skippedSteps?.length || 0);
  const progress = Math.round((doneCount / steps.length) * 100);

  // Get next pending step
  const nextStep = steps.find(step => !completedSteps.includes(step.id) && !skippedSteps.includes(step.id));

  // Avoid initial flicker before tutorial state loads
  if (!isLoaded) return null;
  if (isDismissed || !isOwner || allStepsCompleted) return null;

  return (
    <div className="tutorial-progress-banner">
      <div className="tutorial-banner-content">
        {/* Header Section */}
        <div className="tutorial-banner-header">
          <div className="tutorial-banner-left">
            <div className="tutorial-header-text">
              <div className="tutorial-main-title">
                {nextStep ? `Next: ${nextStep.title}` : 'Setup Complete!'}
              </div>
              <div className="tutorial-subtitle">
                {nextStep ? nextStep.description : 'Your appointment scheduling system is ready to use'}
              </div>
            </div>
          </div>
          
          <div className="tutorial-banner-actions">
            {nextStep && (
              <button 
                className="tutorial-action-btn"
                onClick={nextStep.onClick}
              >
                Get Started
              </button>
            )}
            <button 
              className="tutorial-expand-btn"
              onClick={() => setExpanded(!expanded)}
              aria-label={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? <ChevronUp /> : <ChevronDown />}
            </button>
            <button 
              className="tutorial-close-btn"
              onClick={dismissTutorial}
              aria-label="Dismiss"
            >
              <Close />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="tutorial-progress-section">
          <div className="tutorial-progress-info">
            <span className="tutorial-progress-label">Setup Progress</span>
            <span className="tutorial-progress-percent">{progress}%</span>
          </div>
          <div className="tutorial-progress-bar-container">
            <div 
              className="tutorial-progress-bar-fill" 
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

          {/* Expanded Steps List */}
        {expanded && (
          <div className="tutorial-steps-list">
            {steps.map((step) => {
              const isCompleted = completedSteps.includes(step.id);
              const isSkipped = skippedSteps.includes(step.id);
              const isNext = step.id === nextStep?.id;
              
              return (
                <div
                  key={step.id}
                  className={`tutorial-step-item ${isCompleted ? 'completed' : ''} ${isSkipped ? 'skipped' : ''} ${isNext ? 'next' : ''}`}
                >
                  <div 
                    className="tutorial-step-main"
                    onClick={step.onClick}
                  >
                    <div className="tutorial-step-badge">
                      {isCompleted ? <Check /> : step.id}
                    </div>
                    <div className="tutorial-step-content">
                      <div className="tutorial-step-title">{step.title}</div>
                      <div className="tutorial-step-desc">{step.description}</div>
                    </div>
                  </div>
                  <div className="tutorial-step-actions">
                    {isCompleted && (
                      <div className="tutorial-step-status">
                        <Check />
                      </div>
                    )}
                    {isSkipped && (
                      <div className="tutorial-step-status skipped-label">Skipped</div>
                    )}
                    {!isCompleted && !isSkipped && (
                      <>
                        <button
                          className="tutorial-step-action-btn"
                          onClick={step.onClick}
                        >
                          Go
                        </button>
                        <button
                          className="tutorial-step-skip"
                          onClick={(e) => { e.stopPropagation(); skipStep(step.id); }}
                        >
                          Skip
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

